/**
 * Scan Reports API Service
 * Handles all scan report operations via backend API
 */

import apiClient from './api.js';

class ScanReportsService {
  
  /**
   * Create or update a scan report
   * @param {Object} reportData - Scan report data
   * @returns {Promise<Object>} - Created/updated report response
   */
  async createOrUpdate(reportData) {
    return await apiClient.post('/scan-reports', reportData);
  }

  /**
   * Get scan reports with filtering and pagination
   * @param {Object} filters - Filter options
   * @param {string} filters.type - 'phrase', 'discover', or 'all'
   * @param {string} filters.officerId - Officer email filter
   * @param {string} filters.scannerUid - Firebase UID filter
   * @param {string} filters.source - Source filter
   * @param {string} filters.startDate - ISO start date filter
   * @param {string} filters.endDate - ISO end date filter
   * @param {number} filters.limit - Max results per page (default: 20)
   * @param {number} filters.offset - Skip N results for pagination
   * @param {string} filters.orderBy - Sort field ('createdAt', 'updatedAt')
   * @param {string} filters.order - Sort direction ('asc', 'desc')
   * @returns {Promise<Object>} - Response with reports array and metadata
   */
  async getReports(filters = {}) {
    const params = {
      limit: 20,
      offset: 0,
      orderBy: 'createdAt',
      order: 'desc',
      ...filters
    };

    return await apiClient.get('/scan-reports', params);
  }

  /**
   * Get a single scan report by ID
   * @param {string} reportId - Report ID
   * @returns {Promise<Object>} - Report data
   */
  async getReportById(reportId) {
    return await apiClient.get(`/scan-reports/${reportId}`);
  }

  /**
   * Update fraud counts for a scan report
   * @param {string} reportId - Report ID
   * @param {Object} updateData - Count update data
   * @param {string} updateData.groupKey - Group key to update
   * @param {string} updateData.newStatus - New status ('fake', 'genuine', 'unreviewed')
   * @returns {Promise<Object>} - Update response
   */
  async updateCounts(reportId, updateData) {
    return await apiClient.patch(`/scan-reports/${reportId}/counts`, updateData);
  }

  /**
   * Delete a scan report
   * @param {string} reportId - Report ID
   * @returns {Promise<Object>} - Delete response
   */
  async deleteReport(reportId) {
    return await apiClient.delete(`/scan-reports/${reportId}`);
  }

  /**
   * Get recent reports for a specific scan type
   * @param {string} scanType - 'phrase' or 'discover'
   * @param {number} limit - Max results (default: 10)
   * @returns {Promise<Object>} - Recent reports
   */
  async getRecentReports(scanType, limit = 10) {
    return await this.getReports({
      type: scanType,
      limit,
      orderBy: 'createdAt',
      order: 'desc'
    });
  }

  /**
   * Get reports by officer (for history view)
   * @param {string} officerId - Officer email
   * @param {string} scannerUid - Firebase UID
   * @param {string} scanType - Optional scan type filter
   * @param {number} limit - Results per page
   * @param {number} offset - Pagination offset
   * @returns {Promise<Object>} - Officer's reports
   */
  async getReportsByOfficer(officerId, scannerUid = '', scanType = 'all', limit = 20, offset = 0) {
    return await this.getReports({
      officerId,
      scannerUid,
      type: scanType,
      limit,
      offset
    });
  }

  /**
   * Search reports by keywords (client-side filtering for now)
   * @param {string} query - Search query
   * @param {string} scanType - Optional scan type filter
   * @returns {Promise<Object>} - Matching reports
   */
  async searchReports(query, scanType = 'all') {
    const reports = await this.getReports({
      type: scanType,
      limit: 100 // Get more results for client-side filtering
    });

    // Client-side search through keywords and report data
    const filteredReports = reports.reports.filter(report => {
      const searchText = [
        ...(report.keywords || []),
        report.keywordQuery || '',
        report.reportId || '',
        ...(report.matchedKeywordsFound || [])
      ].join(' ').toLowerCase();

      return searchText.includes(query.toLowerCase());
    });

    return {
      ...reports,
      reports: filteredReports,
      total: filteredReports.length
    };
  }

  /**
   * Get paginated reports (simplified pagination helper)
   * @param {number} page - Page number (1-based)
   * @param {number} perPage - Items per page
   * @param {Object} filters - Additional filters
   * @returns {Promise<Object>} - Paginated results
   */
  async getPaginatedReports(page = 1, perPage = 20, filters = {}) {
    const offset = (page - 1) * perPage;
    
    return await this.getReports({
      ...filters,
      limit: perPage,
      offset
    });
  }
}

// Create singleton instance
const scanReportsService = new ScanReportsService();

export { scanReportsService, ScanReportsService };
export default scanReportsService;
